import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { ClinicalInventory } from "@/lib/clinicalInventory";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";

export type { ClinicalInventory };
/**
 * The failure classifier lives in `lib/clinicalInventory.ts` and is re-exported
 * here, where its callers already look.
 *
 * It is a pure mapping from ONE error envelope to the verdict a screen renders,
 * which is what that module is for — and, decisively, `src/lib` is the half of
 * the web suite that gates a merge (`pr-gate`'s `web-fast`). A classifier whose
 * whole job is telling a persistent refusal from a transient one should not be
 * the part of this feature no gate exercises.
 */
export {
  classifyInventoryReadFailure,
  type InventoryReadFailure,
} from "@/lib/clinicalInventory";

/**
 * The Project's clinical configuration inventory, at ONE identity (#1086
 * consuming #1085).
 *
 * ## ALWAYS RE-READ ON MOUNT
 *
 * `usePreviewCandidatesQuery`'s setting, for the same reason and with a sharper
 * consequence. Every fact in this response is bound to one configuration
 * identity — draft version, pin-set digest, governed digest, base and posture —
 * and the whole point of the screen is that a clinician can trust what it says
 * about the project *now*. A cached answer up to `staleTime` old would report
 * one configuration's origins under a heading that claims to describe the
 * current one, which is exactly the "reloading cannot revert the screen to
 * stale cached origins" criterion. The writes this application makes also
 * invalidate the key; this is the half that covers the writes it did not make —
 * another tab, another clinician, a pin removed since.
 *
 * The read is bounded and free of side effects by contract: it creates no
 * asset, pin, version or session, so re-reading it on every mount costs nothing
 * but the request.
 */
export function useClinicalInventoryQuery(
  projectId: string,
): UseQueryResult<ClinicalInventory> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectClinicalInventory(projectId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/clinical-inventory", {
          params: { path: { projectId } },
          signal,
        }),
      );
    },
    enabled: projectId.length > 0,
    staleTime: 0,
    refetchOnMount: "always",
    // NOTHING IS KEPT BETWEEN MOUNTS, and that is the criterion rather than a
    // tuning choice. `refetchOnMount: "always"` re-reads, but TanStack still
    // mounts with the PREVIOUS response — `isPending: false`, old `data`,
    // `isFetching: true` — so a clinician returning after changing a pin would
    // see the old origins painted under a heading that says "Current
    // configuration" until the refetch settled, and indefinitely if it were
    // held. Mutations deliberately do not invalidate this key (see
    // `queryKeys.projectClinicalInventory`), so nothing else would clear it.
    //
    // `gcTime: 0` drops the entry once the last observer unmounts, so the answer
    // is never reused across visits. It is NOT the guarantee on its own —
    // eviction is scheduled, so a remount in the same tick still finds the
    // entry — and `ProjectContentSources` gates its render on
    // `isFetchedAfterMount` for that reason. This is the lifetime; that is the
    // enforcement.
    gcTime: 0,
  });
}
